import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import _isString from 'lodash/isString';
import _get from 'lodash/get';
import _isObject from 'lodash/isObject';
import cx from 'classnames';

// Components
import InlineTextareaField from './InlineTextareaField/InlineTextareaField';

// Util
import { ptToPx } from '../../../util';
import { getTextColor, getFoilCssProperties } from '../customizationUtils';

// Styles
import './TextLayer.less';

// Local constants
const VERTICAL_ALIGN_TO_ALIGN_ITEMS = {
  top: 'start',
  bottom: 'end',
  middle: 'center',
};

const TEXT_ALIGN_TO_JUSTIFY_CONTENT = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
};

// helper function to find the longest string in an array
// used to help determine which line of textarea is causing a field width error
function getLongestString(arr) {
  return arr.reduce((a, b) => {
    if (a.length > b.length) {
      return a;
    }
    return b;
  });
}

class TextLayer extends React.Component {
  constructor(props) {
    super(props);

    this.widthObserver = this.widthObserver.bind(this);
    this.heightObserver = this.heightObserver.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);

    this.state = {
      heightObserverId: null,
      widthObserverId: null,
      widthError: null,
      heightError: null,
      editing: false,
      initialTextValue: '',
    };
  }

  componentDidMount() {
    const {
      onSetFieldWidthError,
      onRemoveFieldWidthError,
      onSetElementHeightError,
      onRemoveElementHeightError,
    } = this.props;
    if (onSetElementHeightError && onRemoveElementHeightError) {
      this.setState({ heightObserverId: setInterval(this.heightObserver, 50) });
    } else {
      // eslint-disable-next-line no-console
      console.log('No handlers for element height errors!');
    }
    if (onSetFieldWidthError && onRemoveFieldWidthError) {
      this.setState({ widthObserverId: setInterval(this.widthObserver, 50) });
    } else {
      // eslint-disable-next-line no-console
      console.log('No handlers for element width errors!');
    }
  }

  componentDidUpdate(prevProps) {
    const { isActive } = this.props;
    const { editing } = this.state;
    // set editing to false whenever an element becomes inactive
    // users shouldnt be able to edit the content of an element that isnt active
    if (prevProps.isActive && !isActive && editing) {
      // eslint-disable-next-line
      this.setState({
        // eslint-disable-line
        editing: false,
      });
    }
  }

  componentWillUnmount() {
    const { textElement, onRemoveFieldWidthError, onRemoveElementHeightError } = this.props;
    const { widthError, heightError, widthObserverId, heightObserverId } = this.state;

    // when TextLayer component is unmounting, remove any errors associated with it from
    // error state, we dont want to track errors for elements that are no longer being rendered
    // should the element be rendered again and it still has an width or height error,
    // the widthObserver and/or heightObserver methods will catch the error and re-register it in state
    if (onRemoveFieldWidthError && widthError) {
      onRemoveFieldWidthError(textElement.uuid);
    }
    if (onRemoveElementHeightError && heightError) {
      onRemoveElementHeightError(textElement.uuid);
    }

    // make sure to call clearInterval when unmounting
    if (widthObserverId) {
      clearInterval(widthObserverId);
    }
    if (heightObserverId) {
      clearInterval(heightObserverId);
    }
  }

  widthObserver() {
    const {
      textElement: { uuid, width, customizable },
      onSetFieldWidthError,
      onRemoveFieldWidthError,
    } = this.props;
    const { widthError } = this.state;
    if (this.textElement && customizable) {
      const textWidth = this.textElement.offsetWidth;
      const availableElementWidth = Math.floor(ptToPx(width) * 0.94);
      const nextWidthError = textWidth > availableElementWidth;
      if (nextWidthError && !widthError) {
        onSetFieldWidthError(uuid);
        this.setState({
          widthError: true,
        });
      } else if (!nextWidthError && widthError) {
        onRemoveFieldWidthError(uuid);
        this.setState({
          widthError: false,
        });
      }
    }
  }

  heightObserver() {
    const {
      textElement: { height, uuid, customizable },
      onSetElementHeightError,
      onRemoveElementHeightError,
    } = this.props;
    const { heightError } = this.state;
    if (this.textElement && customizable) {
      const textHeight = this.textElement.offsetHeight;
      const nextHeightError = textHeight > ptToPx(height);
      if (nextHeightError && !heightError) {
        onSetElementHeightError(uuid);
        this.setState({
          heightError: true,
        });
      } else if (!nextHeightError && heightError) {
        onRemoveElementHeightError(uuid);
        this.setState({
          heightError: false,
        });
      }
    }
  }

  handleMouseDown(e) {
    const { customizationDeleted, textElement, isActive, setActiveField } = this.props;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)');
    if (
      !customizationDeleted &&
      textElement.customizable &&
      textElement.tag !== 'RECIPIENT_ADDRESS'
    ) {
      if (!isActive) {
        if (isMobile) {
          if (_isObject(e) && e.preventDefault) {
            e.preventDefault();
          }
        } // keep the textbox from focus event & trigger iOS keyboard shift
        setActiveField(textElement.uuid);
      }
      if (this.inlineTextareaField) {
        const inlineTextarea = this.inlineTextareaField.getRenderedComponent();
        if (inlineTextarea && inlineTextarea.focusInput && !isMobile) {
          inlineTextarea.focusInput();
        }
      }
    }
  }

  handleTouchStart(e) {
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)');
    if (isMobile) {
      if (_isObject(e) && e.preventDefault) {
        e.preventDefault();
      }
    }
  }

  handleInputFocus(e) {
    // save initial value to check for a ∆ later
    this.setState({ initialTextValue: e.target.value });
  }

  handleInputBlur(change) {
    const { handleTextBlur } = this.props;
    const { initialTextValue } = this.state;
    // if the text has changed, send to the UNDO stack
    if (initialTextValue !== change.value) {
      handleTextBlur({
        elementUUID: change.elementUUID,
        fieldName: 'body',
        currentValue: initialTextValue,
      });
    }
  }

  render() {
    const {
      textElement,
      editable,
      isActive,
      customizationValues,
      boundingBoxesAdjusted,
      deltas,
      dragging,
      dragHandle,
      handleTextChange,
      enableEnforceCaps,
      foilColor,
      isTextVerticalAlignmentEnabled,
    } = this.props;
    const { editing, heightError, widthError } = this.state;
    const {
      all_caps: enforceCaps,
      uuid,
      body,
      y,
      height,
      layer,
      font: { font_family: fontFamily, font_weight: fontWeight, font_style: fontStyle },
      font_color: color,
      font_size: defaultFontSize,
      character_spacing: characterSpacing,
      horizontal_alignment: horizontalAlignment,
      line_height: lineHeight,
      tag,
      printing_technique: printingTechnique,
      vertical_alignment: verticalAlignment,
      hasCustomizeError,
    } = textElement;

    const displayedTextColor = getTextColor(color, printingTechnique, foilColor);
    const isGuestAdressingPreview = tag === 'RECIPIENT_ADDRESS' && customizationValues;
    let overrideFontSize = null;
    if (isGuestAdressingPreview) {
      const isNameField = body.indexOf('${RECIPIENT_ADDRESS_LINE_1}') !== -1; // eslint-disable-line no-template-curly-in-string
      const field = isNameField ? 'name' : 'address';
      overrideFontSize = _get(customizationValues, ['fontSizeOverrides', field]) || null;
    }
    const fontSize = overrideFontSize || defaultFontSize;

    const letterSpacing = characterSpacing ? characterSpacing * 0.001 : 0;
    const hasError =
      hasCustomizeError !== undefined ? hasCustomizeError : widthError || heightError;

    let background = 'none';
    let borderColor = 'transparent';
    if (dragging) {
      background = 'none';
      borderColor = '#01b4c0';
    } else if (hasError) {
      background = 'rgba(254, 226, 229, 0.6)';
      borderColor = '#c7133e';
    } else if (isActive) {
      background = 'rgba(1, 180, 192, 0.2)';
      borderColor = '#01b4c0';
    }
    const innerTextLayerStyles = {
      position: 'absolute',
      width: '100%',
      height: '100%',
      zIndex: isActive ? 100 : y || layer,
      textAlign: horizontalAlignment.toLowerCase(),
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor,
      background,
      userSelect: 'none',
    };

    // * For Place Cards, we have to account for vertical alignment for text elements
    let verticalAlignmentContainerStyles = {};
    if (isTextVerticalAlignmentEnabled) {
      verticalAlignmentContainerStyles = { whiteSpace: 'break-spaces' };
      if (verticalAlignment) {
        verticalAlignmentContainerStyles = {
          ...verticalAlignmentContainerStyles,
          display: 'flex',
          justifyContent:
            TEXT_ALIGN_TO_JUSTIFY_CONTENT[horizontalAlignment.toLowerCase()] || 'flex-start',
          alignItems: VERTICAL_ALIGN_TO_ALIGN_ITEMS[verticalAlignment.toLowerCase()] || 'baseline', // value is based of possible values for the vertical-align css property
        };
      }
    }

    // not sure if it's necessary to have fontStyles separate from innerTextLayerStyles,
    // leaving separate for now
    const fontStyles = {
      fontFamily: `'${fontFamily}'`,
      fontSize: `${fontSize}pt`,
      fontWeight,
      fontStyle,
      lineHeight: `${lineHeight}pt`,
      letterSpacing: `${letterSpacing}em`,
      color: `#${displayedTextColor}`,
    };
    // IMPORTANT: boundingBoxesAdjustment comes from tags field of page, after line-height updates in the renderer
    // this flag is required for maintaining backwards compatibility with old projects
    const baseTextStyles = {
      display: 'inline-block',
      paddingTop: boundingBoxesAdjusted ? 0 : '6px',
      textTransform: enableEnforceCaps && enforceCaps ? 'uppercase' : 'none',
    };

    // styles for textarea when editing an element, this might be able to be combined in some
    // way with innerTextLayerStyles in the future, since many of the styles are shared
    const textareaStyles = {
      width: '100%',
      minHeight: `${height}pt`,
      zIndex: 110,
      textAlign: horizontalAlignment.toLowerCase(),
      ...fontStyles,
      overflow: 'hidden',
      cursor: isActive ? 'text' : 'pointer',
    };

    // * Foil specific CSS effect
    const foilColorCssProperties = active =>
      active &&
      foilColor &&
      foilColor !== 'none' &&
      printingTechnique === 'CUSTOM_FOIL' &&
      getFoilCssProperties(foilColor);

    // * Since the Foil effect is set on a background property,
    // * every glyph that overflows its container cannot be seen
    // * This duplicated underneath layer with solid color does the trick
    const foilOverflowEffect = (active, content) =>
      active &&
      foilColor &&
      foilColor !== 'none' &&
      printingTechnique === 'CUSTOM_FOIL' && (
        <span
          style={{
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            ...baseTextStyles,
          }}
        >
          {content}
        </span>
      );

    if (_isString(body)) {
      let interpolatedBody = body;
      let recipientAddressTag = false;

      // for recipient addressing previews, interpolate content from guest list data with body
      // right now this is very much hardcoded - in the future a regex might be a good solution to
      // interpolate the data in a smart way, without the need for explicit conditions
      if (isGuestAdressingPreview) {
        // set recipientAddressTag boolean to true so that we know not to render a textarea
        // field for this element. recipient addressing previews are not part of a form so
        // attempting to render a Field component causes an error
        recipientAddressTag = true;
        if (customizationValues.RECIPIENT_ADDRESS_LINE_1) {
          interpolatedBody = interpolatedBody.replace(
            // eslint-disable-next-line no-template-curly-in-string
            '${RECIPIENT_ADDRESS_LINE_1}',
            customizationValues.RECIPIENT_ADDRESS_LINE_1.body
          );
        } else {
          interpolatedBody = interpolatedBody.replace('${RECIPIENT_ADDRESS_LINE_1}', ''); // eslint-disable-line
        }
        if (customizationValues.RECIPIENT_ADDRESS_LINE_2) {
          interpolatedBody = interpolatedBody.replace(
            // eslint-disable-next-line no-template-curly-in-string
            '${RECIPIENT_ADDRESS_LINE_2}',
            customizationValues.RECIPIENT_ADDRESS_LINE_2.body
          ); // eslint-disable-line
        } else {
          interpolatedBody = interpolatedBody.replace('${RECIPIENT_ADDRESS_LINE_2}', ''); // eslint-disable-line
        }
        if (customizationValues.RECIPIENT_ADDRESS_LINE_3) {
          interpolatedBody = interpolatedBody.replace(
            // eslint-disable-next-line no-template-curly-in-string
            '${RECIPIENT_ADDRESS_LINE_3}',
            customizationValues.RECIPIENT_ADDRESS_LINE_3.body
          ); // eslint-disable-line
        } else {
          interpolatedBody = interpolatedBody.replace('${RECIPIENT_ADDRESS_LINE_3}', ''); // eslint-disable-line
        }
        if (customizationValues.RECIPIENT_ADDRESS_LINE_4) {
          interpolatedBody = interpolatedBody.replace(
            // eslint-disable-next-line no-template-curly-in-string
            '${RECIPIENT_ADDRESS_LINE_4}',
            customizationValues.RECIPIENT_ADDRESS_LINE_4.body
          ); // eslint-disable-line
        } else {
          interpolatedBody = interpolatedBody.replace('${RECIPIENT_ADDRESS_LINE_4}', ''); // eslint-disable-line
        }
      }

      // basic idea is to render a textarea field directly over the normal text layer and toggle the transparency
      // so that the user just sees the textarea, it is important to keep the normal element in the DOM
      // in order to set width / height errors if the element becomes to narrow/short for its content
      return (
        <div
          className={cx('text-layer', {
            'text-layer--error': hasError,
          })}
          style={{ ...innerTextLayerStyles, ...fontStyles }}
          onMouseDown={this.handleMouseDown}
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleMouseDown}
        >
          <div
            className={cx('text', {
              'text--active': isActive,
              'text--dragging': dragging,
              'text--editable': editable,
              'text--editing': editing,
            })}
            style={{
              ...{
                top: deltas.top,
                right: deltas.right,
                bottom: deltas.bottom,
                left: deltas.left,
                color: editable && isActive ? 'transparent' : `#${displayedTextColor}`,
              },
              ...verticalAlignmentContainerStyles,
            }}
          >
            <span
              style={{ ...baseTextStyles, ...foilColorCssProperties(!isActive), zIndex: 1 }}
              ref={el => {
                this.textElement = el;
              }}
            >
              {isActive && (
                <span
                  className={cx('text-element-border', {
                    [`text-element-border--${dragHandle}`]: dragHandle,
                    'text-element-border--dragging': dragging,
                  })}
                />
              )}
              {interpolatedBody}
            </span>
            {foilOverflowEffect(!isActive, interpolatedBody)}
          </div>
          {editable && !recipientAddressTag && (
            <div
              className="inline-textarea-field-container"
              style={{
                position: 'absolute',
                top: deltas.top,
                right: deltas.right,
                bottom: deltas.bottom,
                left: deltas.left,
                overflow: 'visible',
              }}
            >
              <Field
                name={`${uuid}.body`}
                component={InlineTextareaField}
                onBlur={(e, newValue) =>
                  this.handleInputBlur({ elementUUID: uuid, fieldName: 'body', value: newValue })
                }
                onFocus={this.handleInputFocus}
                onChange={(e, newValue, previousValue) => {
                  if (
                    widthError &&
                    newValue.length >= previousValue.length &&
                    !(newValue.split('\n').length > previousValue.split('\n').length)
                  ) {
                    const previousValueLongestLine = getLongestString(previousValue.split('\n'));
                    const newValueLongestLine = getLongestString(newValue.split('\n'));
                    if (newValueLongestLine.length > previousValueLongestLine.length) {
                      if (_isObject(e) && e.preventDefault) {
                        e.preventDefault();
                      }
                    }
                  } else {
                    handleTextChange({ elementUUID: uuid, fieldName: 'body', value: newValue });
                  }
                }}
                rows={body ? body.split('\n').length : 1}
                style={{
                  ...baseTextStyles,
                  ...textareaStyles,
                  color: !isActive ? 'transparent' : `#${displayedTextColor}`,
                  ...foilColorCssProperties(isActive),
                }}
                ref={el => {
                  this.inlineTextareaField = el;
                }}
                forwardRef
                withRef // needed to use Field getRenderedComponent() method
              />
            </div>
          )}
        </div>
      );
    }
    return null;
  }
}

TextLayer.propTypes = {
  textElement: PropTypes.shape({}),
  editable: PropTypes.bool,
  isActive: PropTypes.bool,
  customizationValues: PropTypes.shape({}),
  customizationDeleted: PropTypes.bool,
  boundingBoxesAdjusted: PropTypes.bool,
  parentDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  deltas: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  dragging: PropTypes.bool,
  dragHandle: PropTypes.string,
  onSetFieldWidthError: PropTypes.func,
  onRemoveFieldWidthError: PropTypes.func,
  onSetElementHeightError: PropTypes.func,
  onRemoveElementHeightError: PropTypes.func,
  setActiveField: PropTypes.func,
  handleTextBlur: PropTypes.func,
  handleTextChange: PropTypes.func,
  enableEnforceCaps: PropTypes.bool,
  isTextVerticalAlignmentEnabled: PropTypes.bool,
};

export default TextLayer;
